import { useState } from "react";
import { Helmet } from "react-helmet";
import Icon from "../../components/AppIcon";
import Header from "../../components/ui/Header";
import CollaborationForm from "./components/CollaborationForm";
import CollaborationTypeCard from "./components/CollaborationTypeCard";
import ProjectTypeSelector from "./components/ProjectTypeSelector";
import SuccessModal from "./components/SuccessModal";
import SuccessStoryCard from "./components/SuccessStoryCard";
import {
  BudgetRange,
  CollaborationType,
  FormData,
  ProjectType,
  SuccessStory,
  TimelineOption
} from "./types";

const Collaboration = () => {
  const [selectedCollaborationType, setSelectedCollaborationType] =
    useState<string>("");
  const [selectedProjectType, setSelectedProjectType] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);

  const collaborationTypes: CollaborationType[] = [
    {
      id: "equity-partnership",
      title: "Equity Partnership",
      description:
        "Join forces as co-founders with shared ownership and long-term commitment to building something great together.",
      icon: "Handshake",
      benefits: [
        "Shared ownership and decision-making",
        "Long-term commitment and growth",
        "Aligned incentives and goals",
        "Mutual investment in success"]

    },
    {
      id: "contract-work",
      title: "Contract Development",
      description:
        "Professional development services with clear deliverables, timelines, and competitive rates for your project needs.",
      icon: "FileText",
      benefits: [
        "Defined scope and deliverables",
        "Flexible engagement terms",
        "Professional development standards",
        "Clear timeline and milestones"]

    },
    {
      id: "mentorship",
      title: "Mentorship & Learning",
      description:
        "Collaborative learning opportunities where we grow together through knowledge sharing and skill development.",
      icon: "GraduationCap",
      benefits: [
        "Knowledge exchange and growth",
        "Skill development opportunities",
        "Industry insights and guidance",
        "Long-term professional relationship"]

    },
    {
      id: "open-source",
      title: "Open Source Contribution",
      description:
        "Contribute to meaningful open-source projects that benefit the developer community and showcase collaborative skills.",
      icon: "GitBranch",
      benefits: [
        "Community-driven development",
        "Portfolio enhancement",
        "Collaborative problem-solving",
        "Public code contributions"]

    }];


  const projectTypes: ProjectType[] = [
    {
      id: "web-app",
      name: "Web Application",
      description: "Full-stack web applications with modern frameworks",
      icon: "Globe"
    },
    {
      id: "mobile-app",
      name: "Mobile Application",
      description: "Cross-platform or native mobile applications",
      icon: "Smartphone"
    },
    {
      id: "ecommerce",
      name: "E-Commerce Platform",
      description: "Online stores and marketplace solutions",
      icon: "ShoppingCart"
    },
    {
      id: "saas",
      name: "SaaS Product",
      description: "Software as a Service platforms and tools",
      icon: "Cloud"
    },
    {
      id: "api",
      name: "API Development",
      description: "RESTful APIs and backend services",
      icon: "Server"
    },
    {
      id: "other",
      name: "Other Project",
      description: "Custom solutions and unique requirements",
      icon: "Lightbulb"
    }];


  const budgetRanges: BudgetRange[] = [
    {
      id: "equity-only",
      label: "Equity Only",
      value: "equity",
      description: "Partnership with ownership stake"
    },
    {
      id: "under-5k",
      label: "Under $5,000",
      value: "0-5000",
      description: "Small projects and MVPs"
    },
    {
      id: "5k-15k",
      label: "$5,000 - $15,000",
      value: "5000-15000",
      description: "Medium-sized projects"
    },
    {
      id: "15k-30k",
      label: "$15,000 - $30,000",
      value: "15000-30000",
      description: "Large-scale applications"
    },
    {
      id: "30k-plus",
      label: "$30,000+",
      value: "30000+",
      description: "Enterprise solutions"
    },
    {
      id: "flexible",
      label: "Flexible/Negotiable",
      value: "flexible",
      description: "Open to discussion"
    }];


  const timelineOptions: TimelineOption[] = [
    {
      id: "urgent",
      label: "Urgent (1-2 weeks)",
      value: "1-2-weeks",
      description: "Quick turnaround needed"
    },
    {
      id: "short",
      label: "Short-term (1-3 months)",
      value: "1-3-months",
      description: "Standard project timeline"
    },
    {
      id: "medium",
      label: "Medium-term (3-6 months)",
      value: "3-6-months",
      description: "Comprehensive development"
    },
    {
      id: "long",
      label: "Long-term (6+ months)",
      value: "6-plus-months",
      description: "Ongoing partnership"
    },
    {
      id: "flexible",
      label: "Flexible Timeline",
      value: "flexible",
      description: "Adaptable schedule"
    }];


  const successStories: SuccessStory[] = [
    {
      id: "1",
      partnerName: "TechStart Solutions",
      projectName: "E-Learning Platform MVP",
      collaborationType: "Equity Partnership",
      duration: "4 months",
      outcome: "Successful Launch",
      testimonial:
        "Muhammad's technical expertise and dedication were instrumental in bringing our vision to life. His ability to understand our business needs and translate them into a functional product exceeded our expectations.",
      technologies: ["React", "Node.js", "MongoDB", "AWS"],
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f",
      alt: "Modern office workspace with laptop displaying colorful dashboard interface and coffee cup on wooden desk"
    },
    {
      id: "2",
      partnerName: "Local Business Hub",
      projectName: "Inventory Management System",
      collaborationType: "Contract Work",
      duration: "2 months",
      outcome: "40% Efficiency Gain",
      testimonial:
        "Working with Muhammad was a pleasure. He delivered a robust solution that streamlined our operations and provided excellent documentation for future maintenance.",
      technologies: ["TypeScript", "Express", "PostgreSQL", "Docker"],
      image: "https://images.unsplash.com/photo-1723504513873-ee5ac3549bff",
      alt: "Close-up of computer screen showing colorful programming code with syntax highlighting in dark theme"
    },
    {
      id: "3",
      partnerName: "Community Project",
      projectName: "Open Source Task Manager",
      collaborationType: "Open Source",
      duration: "Ongoing",
      outcome: "500+ GitHub Stars",
      testimonial:
        "Muhammad's contributions to our open-source project have been invaluable. His code quality and collaborative spirit make him an excellent team member.",
      technologies: ["React", "Redux", "Firebase", "Material-UI"],
      image: "https://images.unsplash.com/photo-1531537264351-c1952d1db1f5",
      alt: "Team of diverse professionals collaborating around laptop in bright modern office space with plants"
    }];


  const handleFormSubmit = (_data: FormData) => {
    // Show friendly success animation like contact page
    setShowSuccessBanner(true);
    setTimeout(() => setShowSuccessBanner(false), 5000);
    // Keep modal for detailed success feedback if desired
    setShowSuccessModal(true);
  };

  const handleWhatsAppClick = () => {
    window.open("https://wa.me/qr/MIPMGLGF4RZ5O1", "_blank");
  };

  return (
    <>
      <Helmet>
        <title>Collaboration - Muhammad Fotih | Let's Build Together</title>
        <meta
          name="description"
          content="Partner with Muhammad Fotih for your next tech project. Offering equity partnerships, contract development, mentorship, and open-source collaboration opportunities." />

        <meta
          name="keywords"
          content="tech collaboration, software development partnership, equity partnership, contract developer, mentorship, open source" />

      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                <Icon name="Sparkles" size={16} />
                Let's Build Something Amazing Together
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Collaboration Opportunities
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Whether you're looking for a technical co-founder, a skilled
                developer, or a learning partner, I'm excited to explore how we
                can create value together.
              </p>
              {showSuccessBanner && (
                <div className="mb-8 p-4 rounded-lg bg-success/10 border border-success/20 inline-flex items-center gap-3">
                  <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
                  <div className="text-left">
                    <p className="text-success font-medium">Request submitted successfully!</p>
                    <p className="text-sm text-muted-foreground">I'll get back to you shortly.</p>
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="#collaboration-form"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors">

                  <Icon name="FileText" size={20} />
                  Submit Project Inquiry
                </a>
                <button
                  onClick={handleWhatsAppClick}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-accent-foreground rounded-md font-medium hover:bg-accent/90 transition-colors">

                  <Icon name="MessageCircle" size={20} />
                  Quick Chat on WhatsApp
                </button>
              </div>
            </div>

            <section className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Choose Your Collaboration Model
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Select the partnership approach that best fits your project
                  needs and goals
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {collaborationTypes.map((type) =>
                  <CollaborationTypeCard
                    key={type.id}
                    type={type}
                    isSelected={selectedCollaborationType === type.id}
                    onSelect={setSelectedCollaborationType} />

                )}
              </div>
            </section>

            <section className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  What Are You Building?
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Tell me about your project type so I can better understand
                  your needs
                </p>
              </div>
              <ProjectTypeSelector
                types={projectTypes}
                selectedType={selectedProjectType}
                onSelect={setSelectedProjectType} />

            </section>

            <section className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Success Stories
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Real collaborations that delivered results and built lasting
                  partnerships
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {successStories.map((story) =>
                  <SuccessStoryCard key={story.id} story={story} />
                )}
              </div>
            </section>

            <section className="mb-20 bg-surface rounded-lg border border-border p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Zap" size={32} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Fast Response
                  </h3>
                  <p className="text-muted-foreground">
                    Get a response within 24-48 hours on all collaboration
                    inquiries
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Shield" size={32} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Secure & Confidential
                  </h3>
                  <p className="text-muted-foreground">
                    Your project details and business information are kept
                    strictly confidential
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Users" size={32} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Collaborative Approach
                  </h3>
                  <p className="text-muted-foreground">
                    Work together as partners to achieve mutual success and
                    growth
                  </p>
                </div>
              </div>
            </section>

            <section id="collaboration-form" className="scroll-mt-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Start Your Collaboration Journey
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Fill out the form below with your project details, and let's
                  discuss how we can work together
                </p>
              </div>
              <div className="max-w-4xl mx-auto">
                <CollaborationForm
                  projectTypes={projectTypes}
                  budgetRanges={budgetRanges}
                  timelineOptions={timelineOptions}
                  selectedCollaborationType={selectedCollaborationType}
                  onSubmit={handleFormSubmit} />

              </div>
            </section>

            <section className="mt-20 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-8 md:p-12 text-center">
              <Icon
                name="Rocket"
                size={48}
                color="var(--color-primary)"
                className="mx-auto mb-6" />

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to Turn Your Ideas Into Reality?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Don't wait to start building. Whether you have a fully formed
                concept or just an idea, let's have a conversation about how we
                can collaborate to make it happen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleWhatsAppClick}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-md font-medium text-lg hover:bg-primary/90 transition-colors">

                  <Icon name="MessageCircle" size={24} />
                  Let's Chat on WhatsApp
                </button>
              </div>
            </section>
          </div>
        </main>

        <footer className="bg-surface border-t border-border py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-muted-foreground text-sm">
                &copy; {new Date().getFullYear()} Muhammad Fotih Abdullaev. All
                rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a
                  href="https://github.com/Muhammadninja?tab=repositories"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors">

                  <Icon name="Github" size={20} />
                </a>
                <a
                  href="https://www.linkedin.com/in/fotih-abdullaev-901817356/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors">

                  <Icon name="Linkedin" size={20} />
                </a>
                <a
                  href="https://x.com/muhammad640961?s=21"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors">

                  <Icon name="Twitter" size={20} />
                </a>
              </div>
            </div>
          </div>
        </footer>

        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)} />

      </div>
    </>);

};

export default Collaboration;